import { getIntersectionRoot, scrollContainerMediaQuery } from '@theme/scroll-container';
import { createLineReveal, createBlurReveal } from '@theme/spx-text-effects';

/** Progressive enhancement: content stays visible if JS or animation APIs are unavailable. */
class SpxReveal extends HTMLElement {
  static observedAttributes = [
    'data-entrance-animation', 'data-title-animation', 'data-entrance-once',
    'data-animation-duration', 'data-animation-interval',
  ];

  /** @type {IntersectionObserver | undefined} */
  #observer;
  /** @type {MutationObserver | undefined} */
  #mutations;
  /** @type {AbortController | undefined} */
  #controller;
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  #layoutQuery = window.matchMedia('(min-width: 750px)');
  /** @type {Set<HTMLElement>} */
  #observed = new Set();
  /** @type {WeakSet<HTMLElement>} */
  #played = new WeakSet();
  /** @type {WeakSet<HTMLElement>} */
  #skipNextEntrance = new WeakSet();
  /** @type {Set<HTMLElement>} */
  #pending = new Set();
  /** @type {Map<HTMLElement, import('@theme/spx-text-effects').TextEffect>} */
  #animations = new Map();
  /** @type {WeakMap<Element, number>} */
  #flowNext = new WeakMap();
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  #width = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#reducedMotion.addEventListener('change', this.#setup, { signal });
    document.fonts?.addEventListener('loadingdone', this.#syncTargets, { signal });
    this.#layoutQuery.addEventListener('change', this.#setup, { signal });
    scrollContainerMediaQuery.addEventListener('change', this.#setup, { signal });
    this.addEventListener('focusin', this.#revealForInteraction, { signal });
    document.addEventListener('shopify:block:select', this.#revealForInteraction, { signal });
    document.addEventListener('shopify:section:select', this.#revealForInteraction, { signal });

    // Banner copy is cloned from templates and its active panel changes independently.
    this.#mutations = new MutationObserver(this.#syncTargets);
    this.#mutations.observe(this, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'inert'] });
    // Restore measured lines when the available width changes, even within one breakpoint.
    this.#width = this.getBoundingClientRect().width;
    this.#resizeObserver = new ResizeObserver(([entry]) => {
      const width = entry?.borderBoxSize?.[0]?.inlineSize ?? this.getBoundingClientRect().width;
      if (Math.abs(width - this.#width) > 0.5) {
        this.#width = width;
        this.#setup();
      }
    });
    this.#resizeObserver.observe(this);
    document.fonts?.ready.then(() => {
      if (this.isConnected) this.#syncTargets();
    });
    this.#setup();
  }

  /**
   * @param {string} name
   * @param {string | null} previous
   * @param {string | null} value
   */
  attributeChangedCallback(name, previous, value) {
    // Theme editor morphs can update global settings without replacing the element.
    if (previous === value || !this.#controller || !this.isConnected) return;
    this.#played = new WeakSet();
    this.#setup();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#mutations?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#observer?.disconnect();
    this.#observed.clear();
    this.#cancelAnimations();
    this.#clearPending();
    this.#clearInitial();
  }

  #setup = () => {
    this.#flowNext = new WeakMap();
    this.#observer?.disconnect();
    this.#observed.clear();
    this.#cancelAnimations();
    this.#clearPending();
    this.#skipNextEntrance = new WeakSet();
    this.#observer = undefined;
    if (this.dataset.entranceAnimation === 'none' || this.#reducedMotion.matches ||
        !('IntersectionObserver' in window) || !Element.prototype.animate) {
      this.#clearInitial();
      return;
    }

    this.#observer = new IntersectionObserver(this.#onIntersection, {
      root: getIntersectionRoot(),
      threshold: 0,
    });
    this.#syncTargets();
  };

  #syncTargets = () => {
    if (!this.#observer || document.fonts?.status === 'loading') return;
    const textEffect = ['reveal', 'blur'].includes(this.dataset.titleAnimation ?? 'none');
    const selector = this.dataset.entranceAnimation === 'fade'
      ? `[data-reveal-whole]${textEffect ? ', [data-reveal-text]' : ''}`
      : '[data-reveal-item]';
    const targets = new Set([...this.querySelectorAll(selector)].filter((target) => {
      if (this.dataset.entranceAnimation === 'fade') return true;
      const sequence = target.closest('[data-reveal-sequence]');
      // Desktop scene blocks share one timeline, even when their tops enter
      // the viewport in separate frames. Mobile keeps individual scroll reveals.
      if (sequence === target) return this.#layoutQuery.matches;
      return !sequence || !this.#layoutQuery.matches;
    }));
    // Once-only targets are no longer observed, but hidden/removed banner panels
    // must still release their running animations and temporary line wrappers.
    for (const target of this.#animations.keys()) {
      if (!targets.has(target) || target.closest('[aria-hidden="true"], [inert]')) this.#cancelTarget(target);
    }
    for (const target of this.#observed) {
      if (!targets.has(target) || target.closest('[aria-hidden="true"], [inert]')) {
        this.#observer.unobserve(target);
        this.#observed.delete(target);
        this.#cancelTarget(target);
        this.#showTarget(target);
      }
    }
    for (const target of targets) {
      if (!(target instanceof HTMLElement)) continue;
      if (target.closest('[aria-hidden="true"], [inert]') ||
          (this.dataset.entranceOnce !== 'false' && this.#played.has(target)) || this.#observed.has(target)) continue;
      // Prepare before scrolling reveals the element, not after it is painted.
      // Content already on screen stays visible, including late-loaded sections.
      if (target.hasAttribute('data-spx-reveal-initial')) {
        this.#prepareTarget(target);
        target.removeAttribute('data-spx-reveal-initial');
      } else if (this.#isVisible(target)) this.#skipNextEntrance.add(target);
      else this.#prepareTarget(target);
      this.#observed.add(target);
      this.#observer.observe(target);
    }
  };

  /**
   * @param {IntersectionObserverEntry[]} entries
   * @param {IntersectionObserver} observer
   */
  #onIntersection = (entries, observer) => {
    if (observer !== this.#observer) return;
    /** @type {Map<Element, number>} */
    const batchStarts = new Map();
    for (const entry of entries) {
      const target = entry.target;
      if (!entry.isIntersecting || !(target instanceof HTMLElement) ||
          !entry.boundingClientRect.width || !entry.boundingClientRect.height ||
          !this.#observed.has(target) || this.#animations.has(target) ||
          (this.dataset.entranceOnce !== 'false' && this.#played.has(target)) ||
          target.closest('[aria-hidden="true"], [inert]')) continue;
      const group = target.closest('[data-reveal-visible-stagger]');
      if (!group) continue;
      const order = Number(target.dataset.revealItem) || 0;
      batchStarts.set(group, Math.min(batchStarts.get(group) ?? order, order));
    }
    for (const entry of [...entries].sort((a, b) =>
      a.boundingClientRect.top - b.boundingClientRect.top ||
      a.boundingClientRect.left - b.boundingClientRect.left)) {
      const target = entry.target;
      if (!(target instanceof HTMLElement) || !this.#observed.has(target)) continue;
      if (!entry.isIntersecting) {
        if (!this.#animations.has(target) && !this.#isVisible(target)) this.#prepareTarget(target);
        continue;
      }
      if (!entry.boundingClientRect.width || !entry.boundingClientRect.height) continue;
      if (target.closest('[aria-hidden="true"], [inert]')) continue;
      if (this.dataset.entranceOnce !== 'false' && this.#played.has(target)) continue;
      // Translation can cross the observation edge while an entrance is running.
      // Repeated entrances must finish rather than restarting on those crossings.
      if (this.#animations.has(target)) continue;
      this.#played.add(target);
      if (this.dataset.entranceOnce !== 'false') {
        observer.unobserve(target);
        this.#observed.delete(target);
      }
      if (this.#skipNextEntrance.delete(target)) {
        this.#showTarget(target);
        continue;
      }
      this.#showTarget(target);
      if (target.contains(document.activeElement)) continue;
      this.#cancelTarget(target);
      let order = this.dataset.entranceAnimation === 'fade' ? 0 : Number(target.dataset.revealItem) || 0;
      // A newly visible carousel batch starts immediately, regardless of slide index.
      const group = target.closest('[data-reveal-visible-stagger]');
      if (group) order -= batchStarts.get(group) ?? 0;
      const duration = Number(this.dataset.animationDuration) || 1000;
      const interval = Number(this.dataset.animationInterval) || 160;
      // Large cards need more separation than short text items to read as a cascade.
      const intervalMultiplier = Math.max(1, Number(target.dataset.revealIntervalMultiplier) || 1);
      let delay = Math.min(Math.max(order, 0), 4) * interval * intervalMultiplier;
      const flow = target.hasAttribute('data-reveal-flow-item')
        ? target.closest('[data-reveal-flow]') : null;
      if (flow && this.dataset.entranceAnimation !== 'fade') {
        const now = performance.now();
        // Only queue cards arriving together; later rows need no index-based wait.
        delay = Math.max(0, (this.#flowNext.get(flow) ?? now) - now);
        this.#flowNext.set(flow, now + delay + interval * intervalMultiplier);
      }
      const distance = Math.max(0, Number(target.dataset.revealDistance ?? 20) || 0);
      const easing = 'cubic-bezier(0.42, 0, 0.58, 1)';
      const textOptions = {
        duration, delay, interval, easing,
      };
      /** @type {import('@theme/spx-text-effects').TextEffect | null | undefined} */
      let effect;
      if (target.hasAttribute('data-reveal-sequence') && this.#layoutQuery.matches) {
        const items = /** @type {HTMLElement[]} */ (
          [...target.querySelectorAll('[data-reveal-sequence-item]')]
            .filter((item) => item instanceof HTMLElement)
        );
        effect = {
          animations: items.map((item, index) => {
            const gap = interval * Math.max(1, Number(item.dataset.revealIntervalMultiplier) || 1);
            const travel = Math.max(0, Number(item.dataset.revealDistance ?? 20) || 0);
            return item.animate(
              { opacity: [0, 1], translate: [`0 ${travel}px`, '0 0'] },
              { duration, delay: delay + index * gap, easing, fill: 'backwards' }
            );
          }),
          restore: () => {},
        };
      }
      if (target.hasAttribute('data-reveal-text')) {
        if (this.dataset.titleAnimation === 'reveal') effect = createLineReveal(target, textOptions);
        if (this.dataset.titleAnimation === 'blur') effect = createBlurReveal(target, textOptions);
      }
      effect ??= {
        animations: [target.animate(
          { opacity: [0, 1], translate: [`0 ${distance}px`, '0 0'] },
          { duration, delay, easing, fill: 'backwards' }
        )],
        restore: () => {},
      };
      const activeEffect = effect;
      this.#animations.set(target, activeEffect);
      Promise.all(activeEffect.animations.map((animation) => animation.finished)).then(() => {
        if (this.#animations.get(target) !== activeEffect) return;
        activeEffect.restore();
        this.#animations.delete(target);
        if (this.dataset.entranceOnce === 'false' && this.#observed.has(target) && !this.#isVisible(target)) {
          this.#prepareTarget(target);
        }
      }).catch(() => {});
    }
  };

  /** @param {Event} event */
  #revealForInteraction = (event) => {
    // Editor block IDs can live on the original banner slide rather than its cloned copy.
    if (!(event.target instanceof Element) || !(this.contains(event.target) || event.target.contains(this))) return;
    for (const target of this.#observed) {
      this.#played.add(target);
      this.#observer?.unobserve(target);
    }
    this.#observed.clear();
    this.#cancelAnimations();
    this.#clearPending();
    this.#clearInitial();
  };

  #clearInitial() {
    for (const target of this.querySelectorAll('[data-spx-reveal-initial]')) {
      target.removeAttribute('data-spx-reveal-initial');
    }
  }

  /** @param {HTMLElement} target */
  #isVisible(target) {
    const rect = target.getBoundingClientRect();
    const root = getIntersectionRoot()?.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0 &&
      rect.bottom > Math.max(0, root?.top ?? 0) &&
      rect.top < Math.min(window.innerHeight, root?.bottom ?? window.innerHeight) &&
      rect.right > Math.max(0, root?.left ?? 0) &&
      rect.left < Math.min(window.innerWidth, root?.right ?? window.innerWidth);
  }

  /** @param {HTMLElement} target */
  #prepareTarget(target) {
    this.#skipNextEntrance.delete(target);
    target.setAttribute('data-spx-reveal-pending', '');
    this.#pending.add(target);
  }

  /** @param {HTMLElement} target */
  #showTarget(target) {
    target.removeAttribute('data-spx-reveal-pending');
    this.#pending.delete(target);
  }

  #clearPending() {
    for (const target of this.#pending) this.#showTarget(target);
  }

  #cancelAnimations() {
    for (const target of this.#animations.keys()) this.#cancelTarget(target);
  }

  /** @param {HTMLElement} target */
  #cancelTarget(target) {
    const effect = this.#animations.get(target);
    if (!effect) return;
    for (const animation of effect.animations) animation.cancel();
    effect.restore();
    this.#animations.delete(target);
  }
}

if (!customElements.get('spx-reveal')) customElements.define('spx-reveal', SpxReveal);
